// GlobalAutocomplete.tsx
import React from "react";
import { Autocomplete, TextField, Checkbox } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

interface GlobalAutocompleteProps<T> {
  multiple?: boolean;
  label?: string;
  options: T[];
  value: T | T[] | null;
  onChange: (newValue: T | T[] | null) => void;
  placeholder?: string;
  getOptionLabel: (option: T) => string;
  onInputChange?: (value: string) => void;
  error?: boolean;
  helperText?: string;
  size?: "small" | "medium" | "large" | "default" | any;
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const GlobalAutocomplete = <T,>({
  multiple = false,
  label,
  options,
  value,
  onChange,
  placeholder,
  getOptionLabel,
  onInputChange,
  error,
  helperText,
  size = "default",
}: GlobalAutocompleteProps<T>) => {

  const enhancedOptions = multiple
    ? ([{ _id: "all", label: "Select All" }] as any[]).concat(options as any[])
    : options;

  
  const handleChange = (_: any, newValue: any) => {
    if (multiple) {
      // if "Select All" clicked
      const last = newValue[newValue.length - 1];
      if (last?._id === "all") {
        if (value && (value as any[]).length === options.length) {
          // all already selected → unselect all
          onChange([]);
        } else {
          // select all options
          onChange(options as any);
        }
        return;
      }
    }
    onChange(newValue);
  };

  return (
    <Autocomplete
      multiple={multiple}
      freeSolo
      options={enhancedOptions}
      value={value as any}
      onChange={handleChange}
      onInputChange={(_, newValue) => onInputChange?.(newValue)}
      getOptionLabel={(option) => {
        if (typeof option === "string") return option;
        if ((option as any)._id === "all") return "Select All";
        return getOptionLabel(option as T);
      }}
      // isOptionEqualToValue={(option, val) =>
      //   JSON.stringify(option) === JSON.stringify(val)
      // }
      isOptionEqualToValue={(option, val) =>
        (option as any)._id === (val as any)._id
      }
      disableCloseOnSelect={multiple} // only for multi
      openOnFocus
      renderOption={(props, option, { selected }) =>
        multiple ? (
          <li {...props}>
             <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={
                (option as any)._id === "all"
                  ? (value as any[])?.length === options.length // "Select All" checked if all selected
                  : selected
              }
            />
            {(option as any)._id === "all" ? "Select All"
             : typeof option === "string" ? option : getOptionLabel(option as T)}
          </li>
        ) : (
          <li {...props}>
            {typeof option === "string" ? option : getOptionLabel(option as T)}
          </li>
        )
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size={size}
          fullWidth
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
};

export default GlobalAutocomplete;
